import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Row,
  Column,
  Divider,
  PlayButton,
  device,
  typography,
  useRevealTrail,
  Modal,
  mobileGutterMargin,
} from '@landsbankinn/ui';
import React, { Children, useState } from 'react';
import { animated } from 'react-spring';
import { CardGroup, Group } from '../../components/card-group/CardGroup';
import { Img } from '../../components/img/Img';

interface ImagesProps {
  featured?: boolean;
  children: React.ReactNode;
  columns: number;
  isFeaturedAndSquere?: boolean;
}

interface ImageProps {
  title?: React.ReactNode | null;
  copy?: React.ReactNode | null;
  image?: any;
  video?: any;
  percentage?: number;
  ar?: any;
  isSvg?: boolean;
}

const ImageContainer = styled.div`
  position: relative;

  margin-left: ${(props) => `-${props.theme.containerGutter}px`};
  margin-right: ${(props) => `-${props.theme.containerGutter}px`};

  ${mobileGutterMargin}

  @media ${device.tablet} {
    margin: 0;
  }
`;

const Host = styled.div<ImagesProps>`
  ${Group} {
    @media ${device.tablet} {
      ${(props) =>
        props.isFeaturedAndSquere &&
        css`
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          grid-gap: 20px;

          > * {
            max-width: 100%;
            margin: 0 !important;
          }
          > *:first-child {
            grid-row: 1/3;
            grid-column: 1/3;
          }

          > *:nth-child(2) {
            grid-row: 1/2;
            grid-column: 3/4;
          }

          > *:nth-child(3) {
            grid-row: 2/3;
            grid-column: 3/4;
          }
        `}
    }
  }
`;

const Title = styled.h2`
  ${typography.h3};
  margin-bottom: 3px;
  margin-top: 24px;
`;

const Copy = styled.div<{ paddingTop?: boolean }>`
  ${typography.body}
  margin-bottom: 24px;
  ${(props) => props.paddingTop && 'margin-top: 24px;'}

  @media ${device.tablet} {
    padding-right: 28px;
  }
`;

const Video = styled.div`
  position: relative;
  width: 100%;
  &:after {
    display: block;
    content: '';
    padding-top: 56.25%;
  }
  > iframe {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

export const Image = (props: ImageProps) => {
  const [open, setOpen] = useState(false);
  const { title, copy, image, video, ar, percentage, isSvg, ...rest } = props;
  const html = String(video?.html).replace(/src="(.*?)"/, (value, url) => {
    return `src="${url}${
      url.indexOf('?') === -1 ? '?' : '&'
    }autoplay=1&modestbranding=1&listType=user_uploads"`;
  });

  return (
    <animated.div {...rest}>
      <ImageContainer>
        <Img
          src={image}
          ar={ar}
          isFit={!isSvg}
          isSvg={isSvg}
          percentage={percentage ?? 1}
        />
        {video && (
          <PlayButton
            href="#"
            onClick={(e: any) => {
              e.preventDefault();
              setOpen(true);
              return false;
            }}
          />
        )}
      </ImageContainer>
      <Row>
        <Column md={{ width: 10 / 12, offset: { left: 1 / 12 } }}>
          {title && <Title>{title}</Title>}
          {copy && title && <Divider />}
          {copy && <Copy paddingTop={!title}>{copy}</Copy>}
        </Column>
      </Row>
      {video && (
        <Modal visible={open} onClose={() => setOpen(false)}>
          <Video dangerouslySetInnerHTML={{ __html: html }} />
        </Modal>
      )}
    </animated.div>
  );
};

export const Images = ({
  featured,
  children,
  columns,
  isFeaturedAndSquere,
}: ImagesProps) => {
  const { ref, itemsWithStyleProp } = useRevealTrail(children);
  const count = Math.min(
    columns,
    Children.count(children) - Number(featured || 0),
  );

  return (
    <Host
      featured={featured}
      isFeaturedAndSquere={isFeaturedAndSquere}
      ref={ref}
      columns={columns}
    >
      <CardGroup columns={count} isFeatured={featured}>
        {itemsWithStyleProp}
      </CardGroup>
    </Host>
  );
};
